import { useFlag } from '@unleash/proxy-client-vue';
import { useLocalStorage } from '@vueuse/core';
import type { ComputedRef, Ref } from 'vue';
import { computed } from 'vue';

import { usePartitionedStorageKey } from '@rygen/rygen-ui/composables';

import { useCurrentUserStore } from '@/stores/CurrentUser';

export interface UseElasticsearchOptions {
  searchType: 'order' | 'shipment';
}

export interface UseElasticsearchResponse {
  /**
   * Feature flag indicating if Elasticsearch is enabled by the
   * 'elasticsearch-tms-feat-flag' feature flag.
   *
   * `isElasticsearchEnabled` is preferred in most cases. This field should only
   * be used to allow users to opt-in/out of using elasticsearch.
   */
  isElasticsearchEnabledFeatureFlag: ComputedRef<boolean>;

  /**
   * User preference for enabling/disabling Elasticsearch.

   * This value is stored in local storage and partitioned by user. Defaults to
   * the value of the feature flag on first load.
   */
  isElasticsearchEnabledUserPreference: Ref<boolean>;

  /**
   * Indicates if Elasticsearch is currently enabled.
   *
   * Returns true only when both the feature flag is enabled AND the user
   * preference is enabled.
   *
   * Can be set to update the user preference value.
   */
  isElasticsearchEnabled: Ref<boolean>;
}

export const useElasticsearch = (
  opts: UseElasticsearchOptions,
): UseElasticsearchResponse => {
  const { currentUser } = useCurrentUserStore();

  const { storageKey: useNewSearchKey } = usePartitionedStorageKey(
    `use-new-${opts.searchType}-search`,
    { currentUser },
  );

  const _isElasticsearchEnabledFeatureFlag = useFlag(
    'elasticsearch-tms-feat-flag',
  );
  const isElasticsearchEnabledUserPreference = useLocalStorage(
    useNewSearchKey.value,
    _isElasticsearchEnabledFeatureFlag.value,
  );

  const isElasticsearchEnabledFeatureFlag = computed(
    () => _isElasticsearchEnabledFeatureFlag.value,
  );

  const isElasticsearchEnabled = computed({
    get() {
      return (
        isElasticsearchEnabledFeatureFlag.value &&
        isElasticsearchEnabledUserPreference.value
      );
    },
    set(value: boolean) {
      isElasticsearchEnabledUserPreference.value = value;
    },
  });

  return {
    isElasticsearchEnabledFeatureFlag,
    isElasticsearchEnabledUserPreference,
    isElasticsearchEnabled,
  };
};
